import moment from 'moment'

export const transformDate = (timestamp: any) => {
  const finalDate = (moment as any).unix(timestamp)._d
  const finalDateToMadrid = finalDate.toLocaleString('en-US', { timeZone: 'Europe/Madrid' })
  return (moment as any)(finalDateToMadrid, 'MM/DD/YYYY, hh:mm:ss a')._d
}

export const getFieldValue = (value: string | number | null) => {
  return value ? transformDate(value) : null
}

const getDateKeys = (items: any) => {
  if (items && items.length === 0) {
    return []
  }
  const firstItem = items[0]
  const keys = Object.keys(firstItem)
  const dateKeys = keys.filter((key) => key.includes('EPOCH'))
  return dateKeys
}

export const isDate = (value: any) => {
  return typeof value === 'object'
}

export const formatFieldsTypeDateEPOCH = (items: any) => {
  if (items && items.length === 0) {
    return []
  }

  const dateKeys = getDateKeys(items)

  const formattedItems = items.map((item: any) => {
    if (Array.isArray(dateKeys)) {
      dateKeys.forEach((field: any) => {
        item[field] = !isDate(item[field]) ? getFieldValue(item[field]) : item[field]
      })
    } else {
      item[dateKeys] = getFieldValue(item[dateKeys])
    }
    return item
  })
  return formattedItems
}

export const changeDateFormat = (date: string, inputFormat: string, outputFormat: string) => {
  if (date) {
    const momentDate = moment(date, inputFormat)
    return momentDate.format(outputFormat)
  } else {
    return ''
  }
}

export const dateFormat = (date: string | Date, format: string): string => {
  if (typeof date === 'string') {
    date = new Date(date)
  }
  return moment(date).format(format)
}

export const convertToDate = (date: string, format: string): Date => {
  return moment(date, format).toDate()
}

export const convertUTCToLocal = (date: Date, format: string): string => {
  return moment(date).local().format(format)
}

export const addTimeZoneOffset = (date: Date): Date => {
  return moment(date)
    .add(new Date().getTimezoneOffset() * -1, 'm')
    .toDate()
}

/*
 * Adds time to a date
 * @param { Date } date
 * @param { Number } value
 * @param { String } Unit type to add: days | hours | minutes | seconds | milliseconds | month | year
 * @return { Date }
 */
export const addTimeToDate = (date: Date, value: any = 1, unit: string = 'days'): Date => {
  return moment(date).add(value, unit).toDate()
}

export const actualDate = (format: string): string => {
  return moment().format(format)
}

export const getFormattedCurrentDate = () => {
  const currentDate = new Date()
  return currentDate.toISOString()
}

export const getOneWeekBeforeCurrentDate = () => {
  const currentDate = new Date()
  const oneWeekBefore = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000)

  return oneWeekBefore.toISOString()
}

export const getMadridTimeZone = (): number => {
  const madridDate = new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' })
  const madridTime = new Date(madridDate)
  const utcTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'UTC' }))
  const offsetHours = (madridTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60)
  return offsetHours
}

export const getCurrentTimeZone = (): number => {
  return -new Date().getTimezoneOffset() / 60
}

/**
 * Normaliza el valor de fecha considerando el timezone actual
 * Si el timezone es positivo, formatea la fecha a YYYY-MM-DD
 * Si es negativo, añade el offset del timezone en minutos
 * @param dateValue - Valor de fecha a normalizar (string | Date)
 * @returns El valor de fecha normalizado (string | Date)
 */
export const normalizeDateValueByTimezone = (dateValue: string | Date): string | Date => {
  return getCurrentTimeZone() >= 0
    ? changeDateFormat(dateValue as string, '', 'YYYY-MM-DD')
    : addTimeToDate(dateValue as Date, new Date().getTimezoneOffset(), 'minutes')
}

/**
 * Normaliza los filtros de fecha EPOCH para requests al servidor
 * Convierte las fechas al formato correcto considerando el timezone
 * @param filter - Objeto de filtro que puede contener campos EPOCH
 * @returns El filtro modificado con las fechas normalizadas
 */
export const normalizeEpochFilters = (filter: any) => {
  if (!filter?.filter || !Array.isArray(filter.filter) || filter.filter.length === 0) {
    return filter
  }

  filter.filter = filter.filter.map((filterItem: any) => {
    if (filterItem.field?.endsWith('EPOCH')) {
      return {
        ...filterItem,
        value: normalizeDateValueByTimezone(filterItem.value)
      }
    }
    return filterItem
  })

  return filter
}

/**
 * Converts the "Enviado:" date in an email HTML string from UTC/GMT to the user's local timezone.
 * Matches the pattern <b>Enviado:</b> followed by a date in dd/MM/yyyy HH:mm:ss format.
 * @param html - The raw HTML string from the email-to-html endpoint
 * @returns The HTML with the "Enviado:" date converted to local timezone
 */
export const convertEmailSentDateToLocal = (html: string): string => {
  const regex = /(<b>Enviado:<\/b>\s*)(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/i
  return html.replace(regex, (_match, prefix, dateStr) => {
    const localDate = moment.utc(dateStr, 'DD/MM/YYYY HH:mm:ss').local()
    if (!localDate.isValid()) {
      return prefix + dateStr
    }
    return prefix + localDate.format('DD/MM/YYYY HH:mm:ss')
  })
}
